import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from '@/components/Toast.tsx';
import useAuth from '@/hooks/useAuth.tsx';
import { setAxiosPrivateAccessToken } from '@/hooks/useAxiosInterceptors.tsx';
import { axiosPrivate } from '@/lib/axios';
import { type EnhancedClientSummaryResponse } from '@/types/Auth.ts';

type CompleteLoginOptions = {
  silent?: boolean;
};

export function useCompleteLogin() {
  const { t } = useTranslation();
  const { setToken, setClient, setOnboardingModalOpen } = useAuth();

  return useCallback(
    async (accessToken: string, { silent = false }: CompleteLoginOptions = {}) => {
      setAxiosPrivateAccessToken(accessToken);
      setToken(accessToken);

      try {
        const { data } = await axiosPrivate.get<EnhancedClientSummaryResponse>('/clients/current');
        setClient({
          client_details: data.client_details,
          addresses: data.addresses || [],
        });

        if (!silent) {
          showToast({
            type: 'success',
            title: t('successful_login_title'),
            description: t('successful_login_desc', {
              name: data?.client_details?.name,
            }),
          });
        }
      } catch (error) {
        console.error('Failed to load current client after login', error);
      } finally {
        setOnboardingModalOpen(true);
      }
    },
    [setClient, setOnboardingModalOpen, setToken, t],
  );
}
